import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useModalInstance } from '@app/context/ModalInstanceContext'

import { BaseModalType } from '@context/ModalsContext'

import { IconButton } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import { Modal } from '@ui/components/Modal'
import { CopyIcon } from '@ui/icons/Copy.icon'

import { useCopy } from '@hooks/useCopy'
import { usePersistentWindow } from '@hooks/usePersistentWindow'

import { Box } from '@shared/components/Box'

interface Props {
  reference: string
  onCopy?: () => void
  initialCanClose?: boolean
}

export const BankReferenceModal: BaseModalType<Props> = ({
  close,
  reference,
  onCopy,
  initialCanClose = false,
}) => {
  const { t } = useTranslation()
  const { copy } = useCopy()
  const { canClose, setCanClose } = useModalInstance()

  const handleCopy = () => {
    copy(reference, t('Copied'))
    setCanClose(true)
    onCopy?.()
  }

  usePersistentWindow(useCallback(() => !canClose, [canClose]))

  useEffect(() => {
    setCanClose(initialCanClose)
  }, [initialCanClose, setCanClose])

  return (
    <Modal>
      <Modal.Header>{t('Wallet.Don’t forget your reference')}</Modal.Header>
      <Modal.Body>
        <Modal.Body.Text>
          {t(
            'Wallet.Please make sure you include your unique reference when making your bank transfer'
          )}
        </Modal.Body.Text>
        <Box fullWidth flex gap='x8' align='center'>
          <Input fullWidth size='lg' value={reference} readOnly />
          <IconButton.Secondary icon={CopyIcon} onClick={handleCopy} />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary disabled={!canClose} onClick={close}>
          {t('Got it!')}
        </Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal>
  )
}
